import { useState, useRef, useEffect, useCallback } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import type { ChatMessage, MessageRole } from '@domain/chat';
import { normalizeChatMessage } from '@domain/chat';
import { logError } from '@/lib/logger';
import { getCsrfToken } from '@/lib/csrf';

export type ChatStreamMode = 'persistent' | 'ephemeral';

interface UseChatStreamOptions {
  streamUrl: string;
  initialMessages?: ChatMessage[];
  onChatCreated?: (chatId: number) => void;
  onStreamComplete?: (chatId?: number) => void;
  mode?: ChatStreamMode;
  sessionId?: string;
  inactivityTimeoutMs?: number;
  onSessionTimeout?: (reason: 'timeout' | 'manual') => void;
  onEvent?: (event: { type: string; payload?: Record<string, unknown> }) => void;
  bodyFactory?: (content: string) => unknown;
  autoStart?: boolean;
}

export interface UseChatStreamResult {
  input: string;
  setInput: (value: string) => void;
  msgList: StreamMessage[];
  loading: boolean;
  error: string | null;
  streamingMsg: string;
  bottomRef: React.RefObject<HTMLDivElement | null>;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  copiedId: number | null;
  handleCopy: (id: number, content: string) => Promise<void>;
  handleSend: (e: React.FormEvent) => Promise<void>;
  image: File | null;
  setImage: (file: File | null) => void;
  resetConversation: (messages?: ChatMessage[]) => void;
  sessionExpired: boolean;
  remainingTimeMs: number;
  endSessionManually: () => void;
}

export type StreamMessage = {
  id: number;
  role: MessageRole;
  content: string;
  created_at: string;
  image_url?: string | null;
  filename?: string | null;
  content_type?: string | null;
};

const DEFAULT_TIMEOUT = 5 * 60 * 1000;

export const toStreamMessage = (message: ChatMessage, fallbackIndex = 0): StreamMessage => {
  const normalized = normalizeChatMessage(message);
  const raw = normalized as Record<string, unknown>;
  const parsedId =
    typeof normalized.id === 'number' ? normalized.id : Number.parseInt(String(normalized.id), 10);
  const id = Number.isNaN(parsedId) ? Date.now() + fallbackIndex : parsedId;

  return {
    id,
    role: normalized.role,
    content: normalized.content,
    created_at: normalized.createdAt ?? new Date().toISOString(),
    image_url: (raw.image_url as string | null | undefined) ?? null,
    filename: (raw.filename as string | null | undefined) ?? null,
    content_type: (raw.content_type as string | null | undefined) ?? null,
  };
};

export const toStreamMessages = (messages: ChatMessage[] = []): StreamMessage[] =>
  messages.map((message, index) => toStreamMessage(message, index));

function decodeChunk(raw: string) {
  if (!raw.includes('\\')) return raw;
  try {
    return JSON.parse(`"${raw.replace(/"/g, '\\"')}"`);
  } catch {
    return raw.replace(/\\n/g, '\n');
  }
}

export function useChatStream({
  streamUrl,
  initialMessages = [],
  onChatCreated,
  onStreamComplete,
  mode = 'persistent',
  sessionId,
  inactivityTimeoutMs = DEFAULT_TIMEOUT,
  onSessionTimeout,
  onEvent,
  bodyFactory,
  autoStart = false,
}: UseChatStreamOptions): UseChatStreamResult {
  const [input, setInput] = useState('');
  const [msgList, setMsgList] = useState<StreamMessage[]>(() => toStreamMessages(initialMessages));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamingMsg, setStreamingMsg] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const expectFocusRef = useRef(false);
  const autoStartTriggeredRef = useRef(false);

  const timerEnabled = mode === 'ephemeral';
  const [sessionExpired, setSessionExpired] = useState(false);
  const [remainingTimeMs, setRemainingTimeMs] = useState(inactivityTimeoutMs);
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgList, streamingMsg]);

  useEffect(() => {
    if (!loading && streamingMsg === '' && expectFocusRef.current) {
      requestAnimationFrame(() => inputRef.current?.focus());
      expectFocusRef.current = false;
    }
  }, [loading, streamingMsg]);

  useEffect(() => {
    if (!timerEnabled) return;
    lastActivityRef.current = Date.now();
    setSessionExpired(false);
    setRemainingTimeMs(inactivityTimeoutMs);
  }, [sessionId, inactivityTimeoutMs, timerEnabled]);

  useEffect(() => {
    if (!timerEnabled || sessionExpired) return;
    const interval = window.setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(inactivityTimeoutMs - elapsed, 0);
      setRemainingTimeMs(remaining);
      if (remaining === 0) {
        setSessionExpired(true);
        onSessionTimeout?.('timeout');
      }
    }, 1000);
    return () => window.clearInterval(interval);
  }, [timerEnabled, inactivityTimeoutMs, sessionExpired, onSessionTimeout]);

  const markActivity = useCallback(() => {
    if (!timerEnabled) return;
    lastActivityRef.current = Date.now();
    setRemainingTimeMs(inactivityTimeoutMs);
  }, [timerEnabled, inactivityTimeoutMs]);

  type StreamExecutionOptions = {
    content: string;
    autoStart?: boolean;
    includeUserMessage?: boolean;
    attachment?: File | null;
  };

  const executeStream = useCallback(
    async ({ content, autoStart = false, includeUserMessage = true, attachment = null }: StreamExecutionOptions) => {
      if (autoStart && mode === 'ephemeral') {
        setError('La sessione automatica non è disponibile in modalità simulazione.');
        return;
      }

      if (timerEnabled && sessionExpired && !autoStart) {
        setError('La sessione di simulazione è scaduta. Riavvia per continuare.');
        return;
      }

      if (timerEnabled && attachment) {
        setError('Gli allegati non sono disponibili nella simulazione.');
        return;
      }

      const userContent = includeUserMessage ? content : '';
      let optimisticUser: StreamMessage | null = null;

      if (includeUserMessage) {
        optimisticUser = {
          id: Date.now(),
          role: 'user',
          content: userContent,
          created_at: new Date().toISOString(),
        };
        setMsgList((prev) => [...prev, optimisticUser as StreamMessage]);
      }

      setLoading(true);
      setError(null);
      setStreamingMsg('');

      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { 'X-CSRF-Token': csrfToken || '' };
      let body: BodyInit;

      if (mode === 'ephemeral') {
        if (!sessionId) {
          setError('sessionId mancante per la simulazione');
          setLoading(false);
          return;
        }

        const contextPayload = [...msgList, ...(optimisticUser ? [optimisticUser] : [])].map((msg, index) => ({
          role: msg.role,
          content: msg.content,
          index,
        }));

        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({
          message: userContent,
          context: contextPayload,
          session_id: sessionId,
          message_index: contextPayload.length - 1,
        });
      } else if (attachment) {
        const formData = new FormData();
        formData.append('content', content);
        formData.append('image', attachment);
        if (autoStart) {
          formData.append('auto_start', 'true');
        }
        body = formData;
      } else if (bodyFactory) {
        const basePayload = bodyFactory(content) as Record<string, unknown>;
        const payload = autoStart ? { ...basePayload, auto_start: true } : basePayload;
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(payload);
      } else {
        const encodedContent = `content=${encodeURIComponent(content)}`;
        body = autoStart ? `${encodedContent}&auto_start=true` : encodedContent;
        headers['Content-Type'] = 'application/x-www-form-urlencoded';
      }

      onEvent?.({ type: 'request:start', payload: { mode, autoStart } });
      if (timerEnabled) markActivity();

      let botMsg = '';
      let receivedChatId: number | null = null;

      try {
        await fetchEventSource(streamUrl, {
          method: 'POST',
          headers,
          body,
          credentials: 'include',
          onmessage(ev) {
            let rawData = ev.data ?? '';
            rawData = decodeChunk(rawData);
            const trimmed = rawData.trim();

            if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
              try {
                const meta = JSON.parse(trimmed);
                const isMeta =
                  meta &&
                  typeof meta === 'object' &&
                  !Array.isArray(meta) &&
                  (Object.prototype.hasOwnProperty.call(meta, 'event') ||
                    Object.prototype.hasOwnProperty.call(meta, 'id') ||
                    Object.prototype.hasOwnProperty.call(meta, 'type'));

                if (isMeta) {
                  if (meta.type === 'journal_stage') {
                    onEvent?.({ type: 'journal_stage', payload: meta });
                    return;
                  }
                  if (meta.type === 'journal_coach_message') {
                    onEvent?.({ type: 'journal_coach_message', payload: meta });
                    return;
                  }
                  if (typeof meta.id === 'number' || typeof meta.id === 'string') {
                    const parsed =
                      typeof meta.id === 'number' ? meta.id : Number.parseInt(meta.id, 10);
                    if (!Number.isNaN(parsed)) {
                      receivedChatId = parsed;
                      onChatCreated?.(parsed);
                      return;
                    }
                  }
                  if (meta.event === 'simulation_timeout') {
                    setSessionExpired(true);
                    setRemainingTimeMs(0);
                    setError('La sessione di simulazione è scaduta per inattività.');
                    onSessionTimeout?.('timeout');
                    return;
                  }
                  if (meta.event === 'simulation_error') {
                    setError(meta.message || 'Errore nella simulazione');
                    return;
                  }
                }
              } catch {
                // il chunk era contenuto testuale: cade nel branch generico
              }
            }

            botMsg += rawData;
            setStreamingMsg(botMsg);
            if (timerEnabled) markActivity();
          },
          onclose() {
            const cleanMsg = botMsg.replace(/\{\s*"id"\s*:.*?\}\s*$/, '').trim();
            if (cleanMsg.length > 0) {
              const assistantMessage: StreamMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: cleanMsg,
                created_at: new Date().toISOString(),
              };
              setMsgList((prev) => [...prev, assistantMessage]);
            }
            setStreamingMsg('');
            setLoading(false);
            onStreamComplete?.(receivedChatId || undefined);
            onEvent?.({ type: 'request:complete', payload: { mode, autoStart } });
            if (timerEnabled) markActivity();
          },
          onerror() {
            setError('Errore durante la risposta del bot');
            setLoading(false);
            setStreamingMsg('');
            onEvent?.({ type: 'request:error', payload: { mode, autoStart } });
          },
          openWhenHidden: true,
          onopen() {
            return Promise.resolve();
          },
        });
      } catch (err) {
        logError('Errore durante la risposta del bot', err);
        setError('Errore durante la risposta del bot');
        setLoading(false);
        setStreamingMsg('');
        onEvent?.({ type: 'request:error', payload: { mode, autoStart } });
      } finally {
        if (!autoStart) {
          setImage(null);
        }
      }
    },
    [
      bodyFactory,
      markActivity,
      mode,
      msgList,
      onChatCreated,
      onEvent,
      onSessionTimeout,
      onStreamComplete,
      sessionExpired,
      sessionId,
      streamUrl,
      timerEnabled,
    ],
  );

  useEffect(() => {
    if (!autoStart) return;
    if (autoStartTriggeredRef.current) return;
    if (initialMessages.length > 0) return;
    if (msgList.length > 0) return;

    autoStartTriggeredRef.current = true;
    void executeStream({ content: '', autoStart: true, includeUserMessage: false, attachment: null });
  }, [autoStart, executeStream, initialMessages.length, msgList.length]);

  const handleCopy = async (id: number, content: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1000);
  };

  const resetConversation = useCallback(
    (messages: ChatMessage[] = []) => {
      setMsgList(toStreamMessages(messages));
      setStreamingMsg('');
      setError(null);
      setImage(null);
      if (timerEnabled) {
        setSessionExpired(false);
        lastActivityRef.current = Date.now();
        setRemainingTimeMs(inactivityTimeoutMs);
      }
    },
    [inactivityTimeoutMs, timerEnabled],
  );

  const endSessionManually = useCallback(() => {
    if (!timerEnabled) return;
    setSessionExpired(true);
    setRemainingTimeMs(0);
    onSessionTimeout?.('manual');
  }, [timerEnabled, onSessionTimeout]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() && !image) return;
    if (timerEnabled && sessionExpired) {
      setError('La sessione di simulazione è scaduta. Riavvia per continuare.');
      return;
    }
    if (timerEnabled && image) {
      setError('Gli allegati non sono disponibili nella simulazione.');
      return;
    }

    const contentToSend = input;
    const attachment = image;
    setInput('');
    expectFocusRef.current = true;

    await executeStream({
      content: contentToSend,
      autoStart: false,
      includeUserMessage: true,
      attachment,
    });
  };

  return {
    input,
    setInput,
    msgList,
    loading,
    error,
    streamingMsg,
    bottomRef,
    inputRef,
    copiedId,
    handleCopy,
    handleSend,
    image,
    setImage,
    resetConversation,
    sessionExpired,
    remainingTimeMs,
    endSessionManually,
  };
}
